import { createStore } from './createStore';

function _defaultCommon() {
  return {
    prompt: '',
    model: 'auto',
    modelId: null,
    // Store as a string to match existing view controls (seconds dropdown/input).
    durationSeconds: '5',
    // Generic, model-aware UI can validate/normalize these when submitting.
    generationParams: {
      aspectRatio: '16:9',
      resolution: '',
      addAudio: false,
      generateAudio: true,
      cameraFixed: false,
      negativePrompt: '',
      klingMultiPrompt: [],
      klingElements: [],
      klingShotType: 'customize',
      // Motion-control defaults
      referenceVideoUrl: null,
      keepOriginalSound: false,
      characterOrientation: 'image',
      cfgScale: 0.5,
      seed: undefined,
      voiceIds: undefined,
      klingImages: [],
      _klingImagesVersion: 0,
    },
  };
}

function _defaultInputs() {
  return {
    startKeyframeId: null,
    endKeyframeId: null,
    refKeyframeIds: [],
    selectedAssetIds: [],
    cameraMotion: null,
    motionStrength: 50,
    sourceVideoUrl: null,
    strength: 50,
  };
}

function _coerceDurationSecondsFromMs(durationMs) {
  if (typeof durationMs !== 'number' || Number.isNaN(durationMs)) return '5';
  const sec = Math.max(1, Math.round(durationMs / 1000));
  return String(sec);
}

function _normalizeGenerationParams(raw) {
  if (!raw || typeof raw !== 'object') return _defaultCommon().generationParams;
  const def = _defaultCommon().generationParams;
  return {
    aspectRatio:
      typeof raw.aspectRatio === 'string'
        ? raw.aspectRatio
        : (typeof raw.aspect_ratio === 'string' ? raw.aspect_ratio : def.aspectRatio),
    resolution: typeof raw.resolution === 'string' ? raw.resolution : def.resolution,
    addAudio:
      typeof raw.addAudio === 'boolean'
        ? raw.addAudio
        : (typeof raw.add_audio === 'boolean' ? raw.add_audio : def.addAudio),
    generateAudio:
      typeof raw.generateAudio === 'boolean'
        ? raw.generateAudio
        : (typeof raw.generate_audio === 'boolean' ? raw.generate_audio : def.generateAudio),
    cameraFixed:
      typeof raw.cameraFixed === 'boolean'
        ? raw.cameraFixed
        : (typeof raw.camera_fixed === 'boolean' ? raw.camera_fixed : def.cameraFixed),
    negativePrompt:
      typeof raw.negativePrompt === 'string'
        ? raw.negativePrompt
        : (typeof raw.negative_prompt === 'string' ? raw.negative_prompt : def.negativePrompt),
    klingMultiPrompt: Array.isArray(raw.klingMultiPrompt)
      ? raw.klingMultiPrompt
      : (Array.isArray(raw.kling_multi_prompt) ? raw.kling_multi_prompt : def.klingMultiPrompt),
    klingElements: Array.isArray(raw.klingElements)
      ? raw.klingElements
      : (Array.isArray(raw.kling_elements) ? raw.kling_elements : def.klingElements),
    klingShotType: typeof raw.klingShotType === 'string'
      ? raw.klingShotType
      : (typeof raw.kling_shot_type === 'string' ? raw.kling_shot_type : def.klingShotType),
    referenceVideoUrl: typeof raw.referenceVideoUrl === 'string' ? raw.referenceVideoUrl : def.referenceVideoUrl,
    keepOriginalSound: typeof raw.keepOriginalSound === 'boolean' ? raw.keepOriginalSound : def.keepOriginalSound,
    characterOrientation: typeof raw.characterOrientation === 'string'
      ? raw.characterOrientation
      : def.characterOrientation,
    cfgScale:
      typeof raw.cfgScale === 'number'
        ? raw.cfgScale
        : (typeof raw.cfg_scale === 'number' ? raw.cfg_scale : def.cfgScale),
    seed: typeof raw.seed === 'number' ? raw.seed : def.seed,
    voiceIds: Array.isArray(raw.voiceIds) ? raw.voiceIds : def.voiceIds,
    klingImages: Array.isArray(raw.klingImages)
      ? raw.klingImages
      : (Array.isArray(raw.kling_images) ? raw.kling_images : def.klingImages),
    _klingImagesVersion: typeof raw._klingImagesVersion === 'number' ? raw._klingImagesVersion : 0,
  };
}

function _deriveKeyframeIdsFromVideo(video) {
  const elements = video?.elements && typeof video.elements === 'object' ? video.elements : {};
  const start =
    elements.startKeyframeID ||
    elements.startKeyframeId ||
    elements.start_keyframe_id ||
    video?.startKeyframeId ||
    null;
  const end =
    elements.endKeyframeID ||
    elements.endKeyframeId ||
    elements.end_keyframe_id ||
    video?.endKeyframeId ||
    null;
  return {
    startKeyframeId: start ? String(start) : null,
    endKeyframeId: end ? String(end) : null,
  };
}

function _isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function _areDraftValuesEqual(a, b) {
  if (Object.is(a, b)) return true;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => _areDraftValuesEqual(value, b[index]));
  }

  if (_isPlainObject(a) || _isPlainObject(b)) {
    if (!_isPlainObject(a) || !_isPlainObject(b)) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && _areDraftValuesEqual(a[key], b[key]));
  }

  return false;
}

function _mergeDraftUpdates(current, updates) {
  const base = _isPlainObject(current) ? current : {};
  const resolved = _isPlainObject(updates) ? updates : {};
  let changed = false;
  const next = { ...base };

  Object.entries(resolved).forEach(([key, value]) => {
    if (!_areDraftValuesEqual(base[key], value)) {
      next[key] = value;
      changed = true;
    }
  });

  return changed ? next : null;
}

const initialState = {
  activeKey: null,
  draftsByKey: {},
};

/**
 * Draft params store for video creation/editing.
 *
 * Keying strategy:
 * - Editing existing video: key = videoId
 * - Creating new video: key = `create-${creationSessionId}`
 *
 * This lets the model-aware composer preserve entered params even if the canvas remounts.
 */
export const useVideoDraftStore = createStore('VideoDraftStore', (set, get) => ({
  ...initialState,

  setActiveKey: (key) => set({ activeKey: key || null }),

  ensureDraft: (key, overrides = {}) =>
    set((state) => {
      if (!key) return state;
      if (state.draftsByKey[key]) return state;
      return {
        draftsByKey: {
          ...state.draftsByKey,
          [key]: {
            common: _defaultCommon(),
            inputs: _defaultInputs(),
            hydratedFromVideoId: null,
            ...overrides,
          },
        },
      };
    }),

  resetDraft: (key) =>
    set((state) => {
      if (!key || !state.draftsByKey[key]) return state;
      const next = { ...state.draftsByKey };
      delete next[key];
      return { draftsByKey: next };
    }),

  resetAllDrafts: () => set(() => ({ ...initialState })),

  updateCommon: (key, updates) =>
    set((state) => {
      if (!key || !state.draftsByKey[key]) return state;
      const existing = state.draftsByKey[key];
      const resolved = typeof updates === 'function' ? updates(existing.common) : updates;
      if (!resolved) return state;
      const nextCommon = _mergeDraftUpdates(existing.common, resolved);
      if (!nextCommon) return state;
      return {
        draftsByKey: {
          ...state.draftsByKey,
          [key]: {
            ...existing,
            common: nextCommon,
          },
        },
      };
    }),

  updateGenerationParams: (key, updates) =>
    set((state) => {
      if (!key || !state.draftsByKey[key]) return state;
      const existing = state.draftsByKey[key];
      const current = existing.common?.generationParams || {};
      const resolved = typeof updates === 'function' ? updates(current) : updates;
      if (!resolved) return state;
      const generationParams = _mergeDraftUpdates(current, resolved);
      if (!generationParams) return state;
      return {
        draftsByKey: {
          ...state.draftsByKey,
          [key]: {
            ...existing,
            common: {
              ...existing.common,
              generationParams,
            },
          },
        },
      };
    }),

  updateInputs: (key, updates) =>
    set((state) => {
      if (!key || !state.draftsByKey[key]) return state;
      const existing = state.draftsByKey[key];
      const current = existing.inputs || _defaultInputs();
      const resolved = typeof updates === 'function' ? updates(current) : updates;
      if (!resolved) return state;
      const nextInputs = _mergeDraftUpdates(current, resolved);
      if (!nextInputs) return state;
      return {
        draftsByKey: {
          ...state.draftsByKey,
          [key]: {
            ...existing,
            inputs: nextInputs,
          },
        },
      };
    }),

  hydrateFromVideo: (key, video) =>
    set((state) => {
      if (!key) return state;
      const existing = state.draftsByKey[key];
      
      // Skip if already hydrated from this video - preserves user edits during remounts.
      if (existing?.hydratedFromVideoId && existing.hydratedFromVideoId === video?.id) {
        return state;
      }
      
      const current = existing || {
        common: _defaultCommon(),
        inputs: _defaultInputs(),
        hydratedFromVideoId: null,
      };

      const generationParamsRaw =
        video?.elements && typeof video.elements === 'object'
          ? video.elements.generationParams
          : null;
      const generationParams = _normalizeGenerationParams(generationParamsRaw);

      const { startKeyframeId, endKeyframeId } = _deriveKeyframeIdsFromVideo(video);

      const durationSeconds = video?.duration ? _coerceDurationSecondsFromMs(video.duration) : current.common.durationSeconds;

      const next = {
        ...current,
        hydratedFromVideoId: video?.id || null,
        common: {
          ...current.common,
          prompt: typeof video?.prompt === 'string' ? video.prompt : current.common.prompt,
          model: typeof video?.modelId === 'string'
            ? video.modelId
            : (typeof video?.model_id === 'string'
              ? video.model_id
              : (typeof video?.model === 'string' ? video.model : current.common.model)),
          modelId: typeof video?.modelId === 'string'
            ? video.modelId
            : (typeof video?.model_id === 'string' ? video.model_id : current.common.modelId),
          durationSeconds,
          generationParams,
        },
        inputs: {
          ...(current.inputs || _defaultInputs()),
          startKeyframeId,
          endKeyframeId,
          cameraMotion: video?.cameraMotion ?? current.inputs?.cameraMotion ?? null,
          motionStrength: typeof video?.motionStrength === 'number'
            ? video.motionStrength
            : (current.inputs?.motionStrength ?? 50),
          sourceVideoUrl: video?.sourceVideoUrl ?? current.inputs?.sourceVideoUrl ?? null,
          strength: typeof video?.strength === 'number' ? video.strength : (current.inputs?.strength ?? 50),
          refKeyframeIds: Array.isArray(video?.elements?.refKeyframeIDs)
            ? video.elements.refKeyframeIDs.filter(Boolean)
            : Array.isArray(video?.refKeyframeIds)
              ? video.refKeyframeIds.filter(Boolean)
              : (current.inputs?.refKeyframeIds ?? []),
          selectedAssetIds: Array.isArray(video?.elements?.selectedAssetIds)
            ? video.elements.selectedAssetIds.filter(Boolean)
            : (current.inputs?.selectedAssetIds ?? []),
        },
      };

      return {
        draftsByKey: { ...state.draftsByKey, [key]: next },
      };
    }),
}));

